// donut-chart.component.ts

import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ChartOptions, ChartData } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { CombinedCostItem } from '../raw-materials-component/raw-materials.component';

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [NgChartsModule, CommonModule],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DonutChartComponent implements OnInit, OnChanges {
  @Input() allProductCosts: Record<string, CombinedCostItem[]> = {};
  @Input() selectedProductName: string | null = null;
  @Input() categoryMode = false; // When true, breaks down selected product by cost category

  public grandTotal: number = 0;
  public donutData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderColor: '#fff',
        borderWidth: 2,
        hoverOffset: 8,
        borderRadius: 6,
      },
    ],
  };

  public donutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    radius: '100%',
    cutout: '75%',
    layout: {
      padding: window.innerWidth < 768 ? { left: 8, right: 8, top: 0, bottom: 0 } : 0,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'center',
        labels: {
          usePointStyle: true,
          font: { size: window.innerWidth < 768 ? 10 : 13 },
          color: '#4b5563',
          boxWidth: window.innerWidth < 768 ? 7 : 10,
          padding: window.innerWidth < 768 ? 8 : 15,

        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label ?? '';
            const value = typeof ctx.parsed === 'number' ? ctx.parsed : 0;
            const total = (ctx.dataset.data as number[]).reduce(
              (s, v) => s + v,
              0
            );
            const pct = total ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: R${value.toFixed(2)} (${pct}%)`;
          },
        },
        backgroundColor: '#111827',
        titleColor: '#f9fafb',
        bodyColor: '#f9fafb',
        borderColor: '#374151',
        borderWidth: 1,
      },
    },
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.buildChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allProductCosts'] || changes['selectedProductName'] || changes['categoryMode']) {
      this.buildChart();
    }
  }

  private buildChart(): void {
    if (this.categoryMode) {
      this.buildCategoryChart();
      return;
    }
    this.buildProductChart();
  }

  private buildCategoryChart(): void {
    const labels: string[] = [];
    const data: number[] = [];

    const resolvedName = this.selectedProductName || '';
    const costs = resolvedName && this.allProductCosts[resolvedName]
      ? this.allProductCosts[resolvedName]
      : [];

    const categoryTotals: Record<string, number> = {};
    for (const item of costs) {
      const cat = item.category ?? 'Other';
      categoryTotals[cat] = (categoryTotals[cat] ?? 0) + item.unitCost;
    }

    for (const [cat, total] of Object.entries(categoryTotals)) {
      if (total > 0) {
        labels.push(cat);
        data.push(total);
      }
    }

    this.grandTotal = data.reduce((s, v) => s + v, 0);

    const COLORS = ['#007A8F', '#6C7A3E', '#B0213C', '#26284F', '#FFA500', '#008000', '#800080', '#FF1493', '#00CED1', '#B8860B'];
    this.donutData = {
      labels,
      datasets: [{
        data,
        backgroundColor: labels.map((_, i) => COLORS[i % COLORS.length]),
        borderColor: '#fff',
        borderWidth: 2,
        hoverOffset: 8,
        borderRadius: 6,
      }],
    };
    this.cdr.markForCheck();
  }

  private buildProductChart(): void {
    const labels: string[] = [];
    const data: number[] = [];
    let selectedProductTotal = 0;
    let othersTotal = 0;

    if (
      this.selectedProductName &&
      this.allProductCosts[this.selectedProductName]
    ) {
      selectedProductTotal = this.allProductCosts[
        this.selectedProductName
      ].reduce((sum, c) => sum + c.unitCost, 0);
      labels.push(this.selectedProductName);
      data.push(selectedProductTotal);
    }
    for (const productName in this.allProductCosts) {
      if (
        this.allProductCosts.hasOwnProperty(productName) &&
        productName !== this.selectedProductName
      ) {
        othersTotal += this.allProductCosts[productName].reduce(
          (sum, c) => sum + c.unitCost,
          0
        );
      }
    }

    if (
      othersTotal > 0 ||
      (!this.selectedProductName &&
        Object.keys(this.allProductCosts).length > 0)
    ) {
      labels.push('Other Products');
      data.push(othersTotal);
    }

    this.grandTotal = selectedProductTotal + othersTotal;

    const COLORS = [
      '#007A8F', // Color for the selected product
      '#6C7A3E', // Color for 'Other Products'
      '#B0213C',
      '#26284F',
      '#FFA500',
      '#008000',
      '#800080',
      '#FF1493',
      '#00CED1',
      '#B8860B',
    ];

    const backgroundColor = labels.map((_, idx) => COLORS[idx % COLORS.length]);

    if (labels.length === 1 && data.length === 1) {
      this.donutData = {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: [COLORS[0]],
            borderColor: '#fff',
            borderWidth: 2,
            hoverOffset: 8,
            borderRadius: 6,
          },
        ],
      };
    } else {
      this.donutData = {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColor,
            borderColor: '#fff',
            borderWidth: 2,
            hoverOffset: 8,
            borderRadius: 6,
          },
        ],
      };
    }

    this.cdr.markForCheck();
  }

  private getColorForIndex(idx: number): string {
    const COLORS = [
      '#007A8F',
      '#6C7A3E',
      '#B0213C',
      '#26284F',
      '#FFA500',
      '#008000',
      '#800080',
      '#FF1493',
      '#00CED1',
      '#B8860B',
    ];
    return COLORS[idx % COLORS.length];
  }
}
